import { useAuth } from "@hooks/useAuth";
import Link from "next/link";
import { PermissionAction } from "types";

type PermissionGateProps = {
  children: React.ReactNode;
  isSystem?: boolean;
} & ({
  where: (permissions: PermissionAction[]) => boolean;
} | {
  has: PermissionAction
})

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  isSystem = false,
  ...props
}) => {
  const { wherePermitted } = usePermissionRender(isSystem)

  if ("where" in props)
    return wherePermitted(children, props.where) ?? null;

  return wherePermitted(children, ps => ps.includes(props.has)) ?? null;
};

export function usePermissionRender(isSystem = false) {
  const { user, organization } = useAuth();

  const member = organization?.members?.find(m => m.id === user?.id)
  const permissions = member?.permissions ?? []

  function _isPermitted(transformer: (ps: PermissionAction[]) => boolean) {
    if (user?.system_role === "admin") return true;
    if (!isSystem && organization?.created_by === user?.id) return true;
    // if (permissions.includes("admin:impersonate_user")) return true;
    return transformer(permissions) || transformer(user?.system_permissions ?? [])
  }

  function ifPermitted<
    T,
    TOrElseFunc extends ((...args: any[]) => any) | undefined
  >(
    permission: PermissionAction,
    obj: T,
    orElse?: TOrElseFunc
  ): T | (TOrElseFunc extends undefined ? undefined : ReturnType<NonNullable<TOrElseFunc>>) {
    if (_isPermitted(ps => ps?.includes(permission))) return obj;

    return orElse?.()
  }

  function ifPermittedAnd<T>(permission: PermissionAction, and: () => boolean, obj: T) {
    if (_isPermitted(ps => ps?.includes(permission)) && and()) return obj;
  }

  function wherePermitted<T>(obj: T, test: (permissions: PermissionAction[]) => boolean) {
    if (_isPermitted(test)) return obj;
  }

  return {
    ifPermitted,
    wherePermitted,
    ifPermittedAnd,
  }
}

export function LinkIf({ _if, ...props }: React.PropsWithChildren<Parameters<typeof Link>[0] & { _if: PermissionAction }>) {
  const { ifPermitted } = usePermissionRender()
  return ifPermitted(_if, <Link {...props} />, () => props.children)
}
